import { Box, HBox, VBox } from "@ui/box/Box"
import { DS } from "../ds/Kit"
import Button from "@ui/button/Button"
import { Spinner } from "@ui/spinner/Spinner"
import { Typo } from "@ui/text/Typo"
import { formatCurrencyWithNumberToSymbolWithNumber } from "@ui/utils/formatter"
import Decimal from "decimal.js"
import QRCode from "qrcode"
import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { toast } from "react-toastify"
import { GD } from "src/GD"
import { CARD_HOLDER_REJECTED_ERROR_CODE, getFriendlyErrorMessage, isKycAlreadyApproved, isKycPhoneCorrectionError } from "src/utils/errors"
import { isKycInProgress } from "src/utils/kyc"
import { getBinKycPath, getIssuerKycState } from "src/utils/issuerKyc"
import { isRunningInNativeApp, openExternalUrl } from "src/utils/nativePlatform"
import { DependentFormParser } from "./DependentFormParser"
import { openCardIssuanceError } from "./CardIssuanceError"
import { openSimplifiedKyc } from "./SimplifiedKyc"
import { useOptionalIssuer } from "../issuer/IssuerProvider"
import { getIssuerDisplayName } from "../issuer/issuerIdentity"
import { getCardHolderRecovery } from "src/utils/cardHolderRecovery"
import { openIssuerReviewWait } from "./IssuerReviewWait"
import { isIssuerReviewPending, reviewFromError } from "src/utils/issuerReview"
import { CardHolderForm } from "./CardHolderForm"
import { confirmAction } from "../popup/general/ConfirmDialog"

const KycPhoneForm = lazy(() => import("./KycPhoneForm").then(module => ({ default: module.KycPhoneForm })))

const CENTS = 100
const KYC_REQUIRED_ERROR_CODE = 2613
const CARD_HOLDER_INCOMPLETE_ERROR_CODE = 2201
const POLL_INTERVAL_MS = 3000
const MASTERCARD_NETWORK = 3
const PERCENT_DIVISOR = 100
const PERCENT_DECIMAL_PLACES = 2
const BIN_DETAILS_TABLET = "@media (max-width: 620px)"
const BIN_DETAILS_MOBILE = "@media (max-width: 360px)"
const MONEY_FIELDS = [
	"amount",
	"limit_daily",
	"limit_monthly",
	"balance_threshold",
	"balance_topup_target"
] as const

interface CardIssuanceFormProps {
	context?: { card_holder_uid?: string; issuer?: number }
	primaryTitle: string
	secondaryTitle?: string
	onCancel?: () => void
	defaultIssuanceFee: number
	confirmMessage: (feeDisplay: string) => string
	onCreated: (feeDisplay: string) => void
}

const getSelectedBin = (data: Record<string, unknown>): string | null => {
	const value =
		Number(data.network) === MASTERCARD_NETWORK ? data.bin_mastercard : data.bin_visa
	return value === undefined || value === null || value === "" ? null : String(value)
}

const hasNoBinOptions = (data: Record<string, unknown>) => {
	const options = Number(data.network) === MASTERCARD_NETWORK
		? data.$__bin_mastercard_values : data.$__bin_visa_values
	return Array.isArray(options) && options.length === 0
}

const buildPayload = (data: Record<string, unknown>): ICardSaved => {
	const payload = { ...data }
	const bin = getSelectedBin(data)
	if (bin) payload.bin = bin

	for (const field of MONEY_FIELDS) {
		const value = payload[field]
		if (value !== undefined && value !== null && value !== "") {
			payload[field] = new Decimal(Number(value)).mul(CENTS).toNumber()
		}
	}

	return payload as unknown as ICardSaved
}

const Badge = ({ tone, children }: { tone: "info" | "warning"; children: string }) => (
	<Typo
		variant='caption'
		sx={{
			padding: "3px 9px",
			borderRadius: "999px",
			fontWeight: 700,
			whiteSpace: "nowrap",
			...(tone === "info"
				? { color: "#1D4ED8", backgroundColor: "#EFF6FF", border: "1px solid #BFDBFE" }
				: { color: "#9A3412", backgroundColor: "#FFF7ED", border: "1px solid #FED7AA" })
		}}
	>
		{children}
	</Typo>
)

const ADVANCED_FIELDS = ["limit_daily", "limit_monthly", "balance_threshold", "balance_topup_target", "allowed_descriptors", "suspicious_freeze_threshold"]
const ADVANCED_SECTIONS = "[data-form-section]:has([data-form-field='limit_daily'], [data-form-field='balance_threshold'], [data-form-field='allowed_descriptors'])"

// The optional sections stay collapsed until the buyer asks for them.
const formSx = (showAdvanced: boolean) => ({
	[`& ${ADVANCED_SECTIONS}`]: showAdvanced ? {} : { display: "none !important" }
})

const CardPreview = ({ name, network, info, issuerName }: { name: string; network: number; info: IBinInfo | null; issuerName: string }) => {
	const mastercard = network === MASTERCARD_NETWORK
	return (
		<HBox gap='16px' sx={{ alignItems: "center", marginBottom: "20px", [BIN_DETAILS_MOBILE]: { flexDirection: "column", alignItems: "stretch" } }}>
			<VBox
				aria-hidden
				sx={{
					width: "200px",
					minWidth: "200px",
					height: "124px",
					padding: "14px 16px",
					boxSizing: "border-box",
					borderRadius: "14px",
					justifyContent: "space-between",
					color: "#FFFFFF",
					background: mastercard
						? "linear-gradient(135deg, #1F2937 0%, #111827 55%, #7C2D12 100%)"
						: DS.heroGradient,
					boxShadow: "0 10px 24px rgba(30, 27, 75, 0.25)"
				}}
			>
				<HBox sx={{ justifyContent: "space-between", alignItems: "center" }}>
					<Box sx={{ width: "28px", height: "20px", borderRadius: "4px", background: "linear-gradient(135deg, #FDE68A, #D97706)" }} />
					{mastercard ? (
						<HBox>
							<Box sx={{ width: "20px", height: "20px", borderRadius: "50%", backgroundColor: "#EB001B" }} />
							<Box sx={{ width: "20px", height: "20px", borderRadius: "50%", backgroundColor: "#F79E1B", marginLeft: "-8px", opacity: 0.9 }} />
						</HBox>
					) : (
						<Typo sx={{ color: "#FFFFFF", fontWeight: 800, fontStyle: "italic", letterSpacing: "0.04em" }}>VISA</Typo>
					)}
				</HBox>
				<Typo sx={{ color: "#FFFFFF", fontVariantNumeric: "tabular-nums", letterSpacing: "0.12em", fontSize: "0.9rem" }}>
					{`${(info?.bin ?? "••••••").slice(0, 4)} ${(info?.bin ?? "••••••").slice(4, 6)}•• •••• ••••`}
				</Typo>
				<Typo variant='caption' sx={{ color: "rgba(255,255,255,0.85)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
					{name.trim() || "Card name"}
				</Typo>
			</VBox>
			<VBox gap='4px' sx={{ minWidth: 0 }}>
				<Typo sx={{ fontWeight: 700, color: "#0F172A", fontSize: "1.05rem" }}>{name.trim() || "New virtual card"}</Typo>
				<Typo variant='caption' sx={{ color: "#64748B", lineHeight: 1.5 }}>
					{info ? `${issuerName} · ${mastercard ? "Mastercard" : "Visa"} · ${info.currency}` : "Pick a network and BIN to see the price."}
				</Typo>
				<Typo variant='caption' sx={{ color: "#64748B", lineHeight: 1.5 }}>
					Ready to use online right after it is issued.
				</Typo>
			</VBox>
		</HBox>
	)
}

const OrderSummary = ({ info, amount, fallbackCurrency, issuerName, showBin }: { info: IBinInfo; amount: number; fallbackCurrency?: string; issuerName: string; showBin: boolean }) => {
	const currency = info.currency || fallbackCurrency || "USD"
	const money = (cents: number) => formatCurrencyWithNumberToSymbolWithNumber(cents ?? 0, currency)
	const percent = `${((info.topup_commission_percent ?? 0) / PERCENT_DIVISOR).toFixed(PERCENT_DECIMAL_PLACES)}%`
	const feeRule = info.topup_fixed_fee ? `${percent} + ${money(info.topup_fixed_fee)}` : percent
	const loadCents = new Decimal(amount || 0).mul(CENTS).toNumber()
	// ponytail: mirrors the backend's provider + our percent; a company-specific commission override can differ by cents.
	const topupFee = loadCents > 0
		? Math.round(loadCents * (info.topup_commission_percent ?? 0) / PERCENT_DIVISOR / PERCENT_DIVISOR) + (info.topup_fixed_fee ?? 0)
		: 0
	const total = (info.issuance_fee ?? 0) + loadCents + topupFee
	const belowMin = loadCents > 0 && loadCents < (info.topup_min ?? 0)
	const rows: [string, string][] = [
		["Issuance fee (one-time)", money(info.issuance_fee)],
		["Initial load", money(loadCents)],
		[`Top-up fee (${feeRule})`, money(topupFee)]
	]

	return (
		<VBox
			gap='10px'
			sx={{ padding: "16px 18px", borderRadius: "14px", backgroundColor: "#F2F6FB", border: "1px solid #DCE7F2", [BIN_DETAILS_MOBILE]: { padding: "12px" } }}
		>
			<HBox gap='8px' sx={{ alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
				<VBox gap='2px'>
					<Typo variant='medium' sx={{ fontWeight: 700, color: "#0F172A" }}>Order summary</Typo>
					<HBox gap='6px' sx={{ flexWrap: "wrap" }}>
						<Typo variant='caption' sx={{ color: "#64748B" }}>{issuerName}</Typo>
						{showBin ? <Typo variant='caption' sx={{ color: "#94A3B8" }}>{`BIN ${info.bin}`}</Typo> : null}
					</HBox>
				</VBox>
				<HBox gap='6px' sx={{ flexWrap: "wrap" }}>
					{info.need_kyc ? <Badge tone='info'>Identity check</Badge> : null}
					{info.can_block === false ? <Badge tone='warning'>Freeze only — permanent blocking unavailable</Badge> : null}
				</HBox>
			</HBox>
			<VBox gap='6px'>
				{rows.map(([label, value]) => (
					<HBox key={label} sx={{ justifyContent: "space-between", gap: "12px" }}>
						<Typo variant='caption' sx={{ color: "#475569" }}>{label}</Typo>
						<Typo variant='caption' sx={{ color: "#0F172A", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{value}</Typo>
					</HBox>
				))}
			</VBox>
			{info.card_price > 0 && (
				// Taken from the card balance by the issuer; deliberately not part of the total below.
				<Typo variant='caption' sx={{ color: "#475569" }}>{`Card price (issuer): ${money(info.card_price)} — taken from the card balance`}</Typo>
			)}
			<HBox sx={{ justifyContent: "space-between", alignItems: "baseline", gap: "12px", paddingTop: "12px", borderTop: "1px dashed #C7C4F6" }}>
				<Typo sx={{ fontWeight: 600, color: "#1D3F61" }}>Charged from balance</Typo>
				<Typo sx={{ fontWeight: 700, fontSize: "1.35rem", color: "#1D3F61", fontVariantNumeric: "tabular-nums" }}>{money(total)}</Typo>
			</HBox>
			<Typo variant='caption' sx={{ color: belowMin ? "#B91C1C" : "#64748B", lineHeight: 1.45 }}>
				{`Minimum initial load ${money(info.topup_min)}. Later top-ups cost ${feeRule}.`}
			</Typo>
		</VBox>
	)
}

export const CardIssuanceForm = ({
	context,
	primaryTitle,
	secondaryTitle = "Cancel",
	onCancel,
	defaultIssuanceFee,
	confirmMessage,
	onCreated
}: CardIssuanceFormProps) => {
	const workspaceIssuer = useOptionalIssuer()
	const selectedWorkspaceIssuerId = workspaceIssuer?.selectedIssuerId ?? null
	const [kycIssuerName, setKycIssuerName] = useState<IKycIssuer | null>(null)
	const [binsInfo, setBinsInfo] = useState<IBinInfo[]>([])
	const [selectedBin, setSelectedBin] = useState<string | null>(null)
	const [noBinOptions, setNoBinOptions] = useState(false)
	// Private templates arrive with the BIN preselected and hidden; the client never chose it.
	const [binPreset, setBinPreset] = useState(false)
	const [amount, setAmount] = useState(0)
	const [cardName, setCardName] = useState("")
	const [network, setNetwork] = useState(0)
	const [showAdvanced, setShowAdvanced] = useState(false)
	// Private templates may carry no limits or auto top-up fields; then there is nothing to reveal.
	const [hasAdvancedFields, setHasAdvancedFields] = useState(false)
	const [fallbackCurrency, setFallbackCurrency] = useState("USD")
	const [templateIssuanceFee, setTemplateIssuanceFee] = useState<number | null>(null)
	const [templateLoading, setTemplateLoading] = useState(true)
	const [templateFailed, setTemplateFailed] = useState(false)
	const [templateAttempt, setTemplateAttempt] = useState(0)
	const [initialEligibilityResolved, setInitialEligibilityResolved] = useState(false)
	const [kycStatus, setKycStatus] = useState<TKycStatus | null>(null)
	const [kycStage, setKycStage] = useState<TKycStage | null>(null)
	const [kycReason, setKycReason] = useState<string | null>(null)
	const [kycLoading, setKycLoading] = useState(false)
	const [startingKyc, setStartingKyc] = useState(false)
	const [verificationUrl, setVerificationUrl] = useState<string | null>(null)
	const [verificationQrDataUrl, setVerificationQrDataUrl] = useState("")
	const [phoneRequired, setPhoneRequired] = useState(false)
	const [holderSetup, setHolderSetup] = useState<"loading" | "form" | "phone" | "restart" | null>(null)
	const [holderModel, setHolderModel] = useState<ICardHolderResponse | null>(null)
	const pollingTimer = useRef<ReturnType<typeof setInterval> | null>(null)

	const selectedInfo = useMemo(
		() => binsInfo.find(info => String(info.bin) === selectedBin) ?? null,
		[binsInfo, selectedBin]
	)
	const selectedBinIssuer = selectedInfo?.issuer ?? context?.issuer ?? selectedWorkspaceIssuerId
	const pricingIssuer = workspaceIssuer?.issuers.find(item => item.issuer === selectedBinIssuer)
	const pricingIssuerName = Number.isInteger(selectedBinIssuer)
		? getIssuerDisplayName({ issuer: selectedBinIssuer!, issuer_label: kycIssuerName?.issuer === selectedBinIssuer ? kycIssuerName.issuer_label : undefined, name: pricingIssuer?.name || (kycIssuerName?.issuer === selectedBinIssuer ? kycIssuerName.name : undefined) })
		: "Unavailable"
	const requiresKyc = selectedInfo?.need_kyc === true
	const selectedKycKind = getBinKycPath(selectedInfo)
	// Each BIN selection owns its asynchronous status and verification requests.
	// Returning to the same BIN still creates a new scope, so old responses stay stale.
	const kycScope = useMemo(() => ({ active: false, statusRequest: 0, polling: false, reviewing: false }), [requiresKyc, selectedBin, selectedBinIssuer])

	const stopPolling = useCallback(() => {
		if (pollingTimer.current) {
			clearInterval(pollingTimer.current)
			pollingTimer.current = null
		}
	}, [])

	useLayoutEffect(() => {
		kycScope.active = true
		setKycLoading(requiresKyc)
		setKycStatus(null)
		setKycIssuerName(null)
		setKycStage(null)
		setKycReason(null)
		setVerificationUrl(null)
		setVerificationQrDataUrl("")
		setStartingKyc(false)
		setPhoneRequired(false)
		setHolderSetup(null)
		setHolderModel(null)
		return () => {
			kycScope.active = false
			kycScope.statusRequest++
			stopPolling()
		}
	}, [kycScope, requiresKyc, stopPolling])

	const applyKycStatus = useCallback((status: TKycStatus, reason: string | null = null, stage: TKycStage = "our_kyc") => {
		if (!kycScope.active) return
		setKycStatus(status)
		setKycStage(stage)
		setKycReason(reason)
		if (!isKycInProgress(status)) {
			stopPolling()
			setVerificationUrl(null)
			setVerificationQrDataUrl("")
		}
	}, [kycScope, stopPolling])

	const fetchIssuerKycStatus = useCallback(async () => {
		if (!kycScope.active) return null
		const request = ++kycScope.statusRequest
		if (!Number.isInteger(selectedBinIssuer)) {
			applyKycStatus("error", "The selected BIN does not identify an issuer. Refresh the card form and try again.")
			return null
		}
		const res = await GD.REQ_KYC_ISSUERS.request({ issuer: selectedBinIssuer ?? undefined })
		if (!kycScope.active || request !== kycScope.statusRequest) return null
		if (res.error) {
			applyKycStatus("error", getFriendlyErrorMessage(res.error, "Failed to check issuer verification"))
			return null
		}
		const selectedIssuer = res.data.find(item => item.issuer === selectedBinIssuer)
		setKycIssuerName(selectedIssuer ?? null)
		const state = getIssuerKycState(res.data, selectedBinIssuer, selectedKycKind)
		if (!state) {
			applyKycStatus("error", "This verification path is not available for the selected BIN.")
			return null
		}
		let reason: string | null = null
		if (state.status === "declined" || state.status === "error") {
			const details = await GD.REQ_KYC_STATUS.request({ issuer: selectedBinIssuer!, bin: selectedBin ?? undefined })
			if (!kycScope.active || request !== kycScope.statusRequest) return null
			if (details.data) reason = state.stage === "issuer"
				? details.data.provider_kyc_reason
				: details.data.our_kyc_reason
		}
		applyKycStatus(state.status, reason, state.stage)
		if (state.stage === "issuer" && isIssuerReviewPending(state.status)) {
			kycScope.reviewing = true
			stopPolling()
			openIssuerReviewWait(selectedBinIssuer!, selectedIssuer?.review, selectedKycKind, selectedIssuer?.issuer_label || selectedIssuer?.name)
			return null
		}
		return state
	}, [applyKycStatus, kycScope, selectedBin, selectedBinIssuer, selectedKycKind, stopPolling])

	const fetchKycStatus = useCallback(async () => {
		if (!kycScope.active || kycScope.polling || kycScope.reviewing) return null
		kycScope.polling = true
		try {
			const request = ++kycScope.statusRequest
			const res = await GD.REQ_KYC_STATUS.request({ issuer: selectedBinIssuer ?? undefined, bin: selectedBin ?? undefined })
			if (!kycScope.active || request !== kycScope.statusRequest) return null
			if (res.error) {
				applyKycStatus("error", getFriendlyErrorMessage(res.error, "Failed to check KYC status"))
				return null
			}

			if (res.data.stage === "issuer" && isIssuerReviewPending(res.data.provider_kyc_status)) {
				kycScope.reviewing = true
				stopPolling()
				openIssuerReviewWait(selectedBinIssuer!, res.data.review, selectedKycKind, res.data.issuer_label)
				return null
			}
			if (res.data.our_kyc_status === "approved") {
				return await fetchIssuerKycStatus()
			}
			applyKycStatus(res.data.our_kyc_status, res.data.our_kyc_reason, "our_kyc")
			return res.data.our_kyc_status
		} finally {
			kycScope.polling = false
		}
	}, [applyKycStatus, fetchIssuerKycStatus, kycScope, selectedBin, selectedBinIssuer, selectedKycKind, stopPolling])

	const startPolling = useCallback(() => {
		if (!kycScope.active || kycScope.reviewing) return
		stopPolling()
		pollingTimer.current = setInterval(() => {
			void fetchKycStatus()
		}, POLL_INTERVAL_MS)
	}, [fetchKycStatus, kycScope, stopPolling])

	useEffect(() => {
		if (!requiresKyc) return

		let active = true
		setKycLoading(true)
		;(async () => {
			const state = await fetchIssuerKycStatus()
			if (!active) return
			setKycLoading(false)
			setInitialEligibilityResolved(true)
			if (state?.stage === "our_kyc" && isKycInProgress(state.status)) startPolling()
		})()

		return () => {
			active = false
			stopPolling()
		}
	}, [fetchIssuerKycStatus, requiresKyc, selectedBin, selectedBinIssuer, startPolling, stopPolling])

	useEffect(() => stopPolling, [stopPolling])

	useEffect(() => {
		let active = true
		setVerificationQrDataUrl("")
		if (!verificationUrl || isRunningInNativeApp()) {
			setVerificationQrDataUrl("")
			return
		}

		QRCode.toDataURL(verificationUrl, { width: 220, margin: 1 })
			.then(dataUrl => {
				if (active) setVerificationQrDataUrl(dataUrl)
			})
			.catch(() => {
				if (active) setVerificationQrDataUrl("")
			})

		return () => {
			active = false
		}
	}, [verificationUrl])

	const openVerification = useCallback(async (url: string) => {
		try {
			const opened = await openExternalUrl(url)
			if (!opened) toast.info("Allow pop-ups, then use the verification button again")
			return opened
		} catch (error) {
			console.error(error)
			toast.error("Could not open identity verification")
			return false
		}
	}, [])

	const continueKycAfterPhone = useCallback(async () => {
		// The phone form is embedded so this card form stays mounted while KYC is
		// completed on the phone and can keep polling in the original tab.
		setPhoneRequired(false)
		setStartingKyc(true)
		const res = await GD.REQ_KYC_START.request({ issuer: selectedBinIssuer ?? undefined })
		setStartingKyc(false)
		if (res.error) {
			if (isKycPhoneCorrectionError(res.error)) {
				setPhoneRequired(true)
				return
			}
			if (isKycAlreadyApproved(res.error)) {
				await fetchIssuerKycStatus()
				return
			}
			toast.error(getFriendlyErrorMessage(res.error, "Failed to start KYC"))
			return
		}
		const url = res.data.verification_url
		if (!url) {
			toast.error("Verification URL is missing")
			return
		}
		setVerificationUrl(url)
		setKycStatus("pending")
		setKycReason(null)
		if (isRunningInNativeApp()) await openVerification(url)
		startPolling()
		toast.info(isRunningInNativeApp()
			? "Complete identity verification, then return here."
			: "Scan the QR code to complete identity verification. This form will update automatically.")
	}, [fetchIssuerKycStatus, openVerification, selectedBinIssuer, startPolling])

	const startKyc = useCallback(async () => {
		if (!kycScope.active) return
		if (verificationUrl && isKycInProgress(kycStatus)) {
			await openVerification(verificationUrl)
			return
		}

		setStartingKyc(true)
		const res = await GD.REQ_KYC_START.request({ issuer: selectedBinIssuer ?? undefined })
		if (!kycScope.active) return
		setStartingKyc(false)
		if (res.error) {
			if (isKycPhoneCorrectionError(res.error)) {
				if (!kycScope.active) return
				setPhoneRequired(true)
				return
			}
			if (isKycAlreadyApproved(res.error)) {
				await fetchIssuerKycStatus()
				return
			}
			toast.error(getFriendlyErrorMessage(res.error, "Failed to start KYC"))
			return
		}

		const url = res.data.verification_url
		if (!url) {
			toast.error("Verification URL is missing")
			return
		}

		setVerificationUrl(url)
		setKycStatus("pending")
		setKycReason(null)
		if (isRunningInNativeApp()) await openVerification(url)
		startPolling()
	}, [
		fetchIssuerKycStatus,
		kycScope,
		kycStatus,
		openVerification,
		selectedBinIssuer,
		startPolling,
		verificationUrl
	])

	const routeHolderModel = useCallback((model: ICardHolderResponse, forceEdit = false): boolean => {
		const missing = Array.isArray(model.missing_fields) ? model.missing_fields : []
		setHolderModel(model)
		if (missing.length === 0 && !forceEdit) return false
		if (selectedKycKind === "simplified") {
			const editable = new Set(["mobile", "area_code", "address.city_code"])
			setHolderSetup(forceEdit || missing.every(field => editable.has(field)) ? "phone" : "restart")
		} else if (missing.some(field => field.startsWith("kyc_vendor"))) {
			setHolderSetup(null)
			toast.error("This card type cannot be registered automatically. Please contact support.")
		} else if (missing.some(field => field.startsWith("document"))) {
			setHolderSetup("restart")
		} else {
			setHolderSetup("form")
		}
		return true
	}, [selectedKycKind])

	const submitHolder = useCallback(async () => {
		const issuer = selectedBinIssuer ?? undefined
		setHolderSetup("loading")
		const response = await GD.REQ_CARDHOLDER_SUBMIT.request({ issuer, bin: selectedBin ?? undefined })
		if (!kycScope.active) return { error: { code: 0, message: "Card form changed" } }
		if (response.error) {
			if (response.error.code === KYC_REQUIRED_ERROR_CODE && isIssuerReviewPending(response.error.holder_status)) {
				applyKycStatus(response.error.holder_status as TKycStatus, null, "issuer")
				setHolderSetup(null)
				openIssuerReviewWait(selectedBinIssuer!, reviewFromError(response.error), selectedKycKind)
				return response
			}
			if (response.error.code === CARD_HOLDER_INCOMPLETE_ERROR_CODE) {
				const form = await GD.REQ_CARDHOLDER_FORM.request({ issuer, bin: selectedBin ?? undefined })
				if (form.data && routeHolderModel(form.data)) return response
			}
			setHolderSetup(null)
			toast.error(getFriendlyErrorMessage(response.error, "Could not submit card holder details"))
			return response
		}

		if (response.data.status === "approved") {
			applyKycStatus("approved", null, "issuer")
			setHolderSetup(null)
		} else if (response.data.status === "under_review") {
			applyKycStatus("under_review", response.data.reason, "issuer")
			setHolderSetup(null)
			void fetchIssuerKycStatus()
		} else {
			applyKycStatus("declined", response.data.reason, "issuer")
			setHolderSetup(null)
		}
		return response
	}, [applyKycStatus, fetchIssuerKycStatus, kycScope, routeHolderModel, selectedBin, selectedBinIssuer, selectedKycKind])

	const openHolderSetup = useCallback(async () => {
		setHolderSetup("loading")
		const response = await GD.REQ_CARDHOLDER_FORM.request({ issuer: selectedBinIssuer ?? undefined, bin: selectedBin ?? undefined })
		if (!kycScope.active) return
		if (response.error) {
			setHolderSetup(null)
			toast.error(getFriendlyErrorMessage(response.error, "Could not load card holder details"))
			return
		}
		const amendRejectedHolder = kycStage === "issuer" && kycStatus === "declined"
		if (!routeHolderModel(response.data, amendRejectedHolder)) await submitHolder()
	}, [kycScope, kycStage, kycStatus, routeHolderModel, selectedBin, selectedBinIssuer, submitHolder])

	const syncFormData = useCallback((data: Record<string, unknown>) => {
		const bin = getSelectedBin(data)
		setSelectedBin(current => (current === bin ? current : bin))
		setNoBinOptions(hasNoBinOptions(data))
		setAmount(Number(data.amount) || 0)
		setCardName(String(data.name ?? ""))
		setNetwork(Number(data.network) || 0)
	}, [])

	const feeCents = selectedInfo?.issuance_fee ?? templateIssuanceFee
	const feeDisplay =
		feeCents !== null
			? formatCurrencyWithNumberToSymbolWithNumber(
					feeCents,
					selectedInfo?.currency || fallbackCurrency
				)
			: `$${defaultIssuanceFee}`
	const issuerApproved = kycStage === "issuer" && kycStatus === "approved"
	const holderDetailsReady = kycStage === "ready"
	const kycBlocked = requiresKyc && (kycLoading || !issuerApproved)
	const kycActionBusy = kycLoading || startingKyc
	const kycInProgress = isKycInProgress(kycStatus)
	const checkingEligibility = templateLoading || (!initialEligibilityResolved && requiresKyc && kycLoading)
	const primaryButton = templateFailed
		? { title: "Retry card options", onClick: () => {
			setTemplateFailed(false)
			setTemplateLoading(true)
			setTemplateAttempt(attempt => attempt + 1)
		} }
		: templateLoading
		? { title: primaryTitle, disabled: true }
		: noBinOptions
		? { title: "No card options available", disabled: true }
		: !kycBlocked
		? { title: primaryTitle }
		: kycLoading || kycStatus === null
			? { title: "Checking Identity…", disabled: true }
			: holderDetailsReady || kycStage === "issuer" && (kycStatus === "declined" || kycStatus === "error")
				? {
						title: holderDetailsReady
							? "Continue"
							: kycStatus === "declined"
								? "Update verification details"
								: "Review verification details",
						disabled: holderSetup === "loading",
						onClick: () => void openHolderSetup()
					}
			: kycInProgress
				? { title: "Identity Check in Progress", disabled: true }
				: {
						title: startingKyc ? "Starting Identity Check…" : "Verify Identity to Create Card",
						disabled: kycActionBusy,
						onClick: () => void startKyc()
					}

	const renderKycGate = () => {
		if (!requiresKyc) return null

		let title = "Identity verification"
		let description =
			"Verify your identity before creating this card."
		if (kycLoading) {
			title = "Checking verification…"
			description = "You can create the card after verification is approved."
		} else if (holderDetailsReady) {
			title = "Identity verification complete"
			description = "Complete the card holder details before creating the card."
		} else if (issuerApproved) {
			title = "Card holder approved"
			description = "The issuer approved the card holder. You can now create the card."
		} else if (isKycInProgress(kycStatus)) {
			title = kycStage === "issuer" ? "Issuer is checking your details" : "Identity verification in progress"
			description = verificationUrl
				? isRunningInNativeApp()
					? 'The selected card issuer still requires verification. Tap "Open identity verification" to continue.'
					: 'The selected card issuer still requires verification. Continue in your verification tab or click "Open identity verification" below.' +
						(verificationQrDataUrl ? " Alternatively, scan the QR code to continue on your phone." : "")
				: "The selected card issuer still requires verification. This card form stays open and updates automatically; you can also resume verification or refresh the status here."
		} else if (kycStatus === "declined" || kycStatus === "expired" || kycStatus === "error") {
			title =
				kycStatus === "declined"
					? kycStage === "issuer" ? "Previous verification was declined" : "Verification declined"
					: kycStatus === "expired"
						? "Verification expired"
						: "Verification needs attention"
			description = kycStage === "issuer" && kycStatus === "declined"
				? `${kycReason ? `${kycReason} ` : ""}Update and resubmit your details to continue.`
				: kycReason || "Start identity verification again to continue."
		}

		const needsAttention = kycStatus === "declined" || kycStatus === "expired" || kycStatus === "error"
		const positive = holderDetailsReady || issuerApproved
		const gateBackground = positive ? "#F0FDF4" : needsAttention ? "#FFF7ED" : "#F8FAFC"
		const gateBorder = positive ? "#BBF7D0" : needsAttention ? "#FED7AA" : "#E2E8F0"
		const gateColor = positive ? "#166534" : needsAttention ? "#9A3412" : "#334155"

		return (
			<VBox
				gap={positive ? "4px" : "10px"}
				sx={{
					padding: "12px 14px",
					borderRadius: "12px",
					backgroundColor: gateBackground,
					border: `1px solid ${gateBorder}`
				}}
			>
				<Typo
					variant='medium'
					sx={{
						fontWeight: 700,
						color: gateColor,
						fontSize: "0.9rem"
					}}
				>
					{title}
				</Typo>
				<Typo
					variant='caption'
					sx={{
						color: gateColor,
						lineHeight: 1.45
					}}
				>
					{description}
				</Typo>
				{!kycLoading && kycInProgress ? (
					<HBox
						gap='16px'
						sx={{
							alignItems: "center",
							justifyContent: "space-between",
							flexWrap: "wrap"
						}}
					>
						<HBox gap='8px' sx={{ flexWrap: "wrap" }}>
							{verificationUrl ? (
								<Button
									title='Open identity verification'
									variant='primary'
									onClick={() => openVerification(verificationUrl)}
								/>
							) : (
								<Button
									title={startingKyc ? "Resuming identity verification…" : "Resume identity verification"}
									variant='primary'
									disabled={startingKyc}
									onClick={() => void startKyc()}
								/>
							)}
							<Button
								title='Refresh status'
								variant='secondary'
								onClick={() => void fetchIssuerKycStatus()}
							/>
						</HBox>
						{verificationQrDataUrl && verificationUrl ? (
							<VBox
								gap='6px'
								sx={{
									alignItems: "center",
									[BIN_DETAILS_TABLET]: { width: "100%" }
								}}
							>
								<Box
									sx={{
										padding: "6px",
										borderRadius: "10px",
										backgroundColor: "#FFFFFF",
										border: "1px solid #E2E8F0"
									}}
								>
									<img
										src={verificationQrDataUrl}
										alt='Identity verification QR code'
										width={112}
										height={112}
									/>
								</Box>
								<Typo variant='caption' sx={{ color: gateColor, textAlign: "center" }}>
									Or continue on your phone
								</Typo>
							</VBox>
						) : null}
					</HBox>
				) : null}
			</VBox>
		)
	}

	return (
		<>
			{phoneRequired ? (
				<Box
					sx={{
						padding: "14px",
						border: "1px solid #CBD5E1",
						borderRadius: "12px",
						backgroundColor: "#F8FAFC"
					}}
				>
					<Suspense fallback={<Typo variant='caption'>Loading phone form…</Typo>}>
						<KycPhoneForm
							issuer={selectedBinIssuer ?? undefined}
							onSuccess={continueKycAfterPhone}
							onCancel={() => setPhoneRequired(false)}
							embedded
						/>
					</Suspense>
				</Box>
			) : null}
			{holderSetup && !phoneRequired ? (
				<Box sx={{ padding: "14px", border: "1px solid #CBD5E1", borderRadius: "12px", backgroundColor: "#F8FAFC" }}>
					{holderSetup === "loading" ? (
						<VBox role='status' aria-label='Loading card holder details' gap='10px' sx={{ minHeight: "160px", alignItems: "center", justifyContent: "center" }}>
							<Spinner size={38} color='#2563EB' />
							<Typo>Checking card holder details…</Typo>
						</VBox>
					) : holderSetup === "phone" ? (
						<Suspense fallback={<Typo variant='caption'>Loading phone form…</Typo>}>
							<KycPhoneForm issuer={selectedBinIssuer ?? undefined} onSuccess={openHolderSetup} onCancel={() => setHolderSetup(null)} embedded />
						</Suspense>
					) : holderSetup === "restart" ? (
						<VBox gap='12px'>
							<Typo sx={{ fontWeight: 700 }}>Identity details are incomplete</Typo>
							<Typo variant='caption'>Some required details were not supplied by identity verification. Start it again to continue.</Typo>
							<HBox gap='8px'>
								<Button title='Restart identity verification' onClick={() => { setHolderSetup(null); void startKyc() }} />
								<Button title='Back' variant='secondary' onClick={() => setHolderSetup(null)} />
							</HBox>
						</VBox>
					) : holderModel ? (
						<CardHolderForm
							missingFields={holderModel.missing_fields}
							issuer={selectedBinIssuer ?? undefined}
							primaryButton={{ title: "Submit card holder details" }}
							secondaryButton={{ title: "Back", variant: "secondary", onClick: () => setHolderSetup(null) }}
							onRequest={async () => holderModel}
							onSubmit={async data => {
								const updated = await GD.REQ_CARDHOLDER_COMPLETION_UPDATE.request(data)
								if (updated.error) return updated
								return submitHolder()
							}}
						/>
					) : null}
				</Box>
			) : null}
			{checkingEligibility && !phoneRequired && !holderSetup ? (
				<VBox
					role='status'
					aria-label='Checking card eligibility'
					gap='12px'
					sx={{ minHeight: "220px", alignItems: "center", justifyContent: "center", textAlign: "center" }}
				>
					<Spinner size={44} color='#2563EB' />
					<VBox gap='5px' sx={{ alignItems: "center" }}>
						<Typo sx={{ fontWeight: 700, color: "#0F172A" }}>Checking card eligibility…</Typo>
						<Typo variant='caption' sx={{ color: "#64748B", lineHeight: 1.5 }}>
							Loading the card options and verification status.
						</Typo>
					</VBox>
				</VBox>
			) : null}
			<Box sx={{ display: phoneRequired || checkingEligibility || holderSetup ? "none" : "block", ...formSx(showAdvanced) }}>
				<CardPreview name={cardName} network={network} info={selectedInfo} issuerName={pricingIssuerName} />
		<DependentFormParser
			key={templateAttempt}
			dependencies={[]}
			extraDeps={["network", "bin_visa", "bin_mastercard"]}
			onDataChange={syncFormData}
			onRequest={async () => {
				try {
					const res = await GD.REQ_GET_CARD_TEMPLATE.request(context ?? {})
					if (!res.data) {
						setTemplateFailed(true)
						return {
							error: { message: "Could not load card options. Please try again.", code: 0, show: true }
						}
					}

					const info = Array.isArray(res.data.bins_info) ? res.data.bins_info : []
					setTemplateFailed(false)
					setBinsInfo(info)
					const template = res.data as unknown as Record<string, unknown>
					setBinPreset(Boolean(template.$__bin_visa_hidden || template.$__bin_mastercard_hidden))
					setHasAdvancedFields(ADVANCED_FIELDS.some(field => field in template && !template[`$__${field}_hidden`]))
					setTemplateIssuanceFee(res.data.issuance_fee ?? null)
					setFallbackCurrency(res.data.currency || "USD")
					syncFormData(template)
					return res.data
				} catch {
					setTemplateFailed(true)
					return { error: { message: "Could not load card options. Please try again.", code: 0, show: true } }
				} finally {
					setTemplateLoading(false)
				}
			}}
			onSubmit={async data => {
				if (hasNoBinOptions(data)) return { error: { code: 0, message: "No cards are available for this network. Choose another network or issuer account.", show: true } }
				const currentBin = getSelectedBin(data)
				const info = binsInfo.find(item => String(item.bin) === currentBin)
				if (info?.need_kyc && (kycLoading || currentBin !== selectedBin || kycStage !== "issuer" || kycStatus !== "approved")) {
					return {
						error: {
							code: KYC_REQUIRED_ERROR_CODE,
							message: "Complete identity verification before creating this card"
						}
					}
				}

				const amountCents = new Decimal(Number(data.amount || 0)).mul(CENTS)
				if (info?.need_kyc && amountCents.lessThan(info.topup_min)) {
					return {
						error: {
							code: 2500,
							message: `Initial balance must be at least ${formatCurrencyWithNumberToSymbolWithNumber(
								info.topup_min,
								info.currency || fallbackCurrency
							)} for BIN ${info.bin}`
						}
					}
				}

				// Explicit confirmation remains immediately before the creation request.
				if (!(await confirmAction({ title: "Confirm card issuance", message: confirmMessage(feeDisplay), confirm: "Create card" }))) {
					return { error: { message: "Cancelled by user", code: 0 } }
				}

				const payload = buildPayload(data)
				const res = await GD.REQ_CREATE_CARD.request(payload)
				// Errors on collapsed optional fields must be visible to be fixable.
				if (res.error && ADVANCED_FIELDS.some(field => JSON.stringify(res.error).includes(field))) setShowAdvanced(true)
				if (!res.error) {
					onCreated(feeDisplay)
					return res
				}

				if (res.error.code === CARD_HOLDER_REJECTED_ERROR_CODE) {
					openCardIssuanceError(res.error)
					return res
				}

				if (res.error.code === CARD_HOLDER_INCOMPLETE_ERROR_CODE) {
					// Some 2201 responses identify the rejecting issuer only in the
					// human-readable details. Keep the BIN issuer as the structured
					// context for every recovery request when `error.issuer` is absent.
					const issuerError = {
						...res.error,
						issuer: res.error.issuer ?? info?.issuer ?? selectedBinIssuer ?? undefined
					}
					const retry = () => GD.REQ_CREATE_CARD.request(payload)
					const recovery = getCardHolderRecovery(issuerError)
					if (recovery === "kyc") {
						if (selectedKycKind === "standard") {
							await openHolderSetup()
							return res
						}
						openSimplifiedKyc(
							retry,
							() => onCreated(feeDisplay),
							issuerError,
							{ forceOurKycRestart: true }
						)
					} else if (recovery === "phone") {
						const { openKycPhoneForm } = await import("./KycPhoneForm")
						openKycPhoneForm(issuerError.issuer, () =>
							retry().then(async retryResponse => {
								if (!retryResponse.error) onCreated(feeDisplay)
								else if (retryResponse.error.code === CARD_HOLDER_INCOMPLETE_ERROR_CODE) {
									const nextError = { ...retryResponse.error, issuer: issuerError.issuer }
									const nextRecovery = getCardHolderRecovery(nextError)
									if (nextRecovery === recovery) {
										openCardIssuanceError(nextError)
									} else if (nextRecovery === "kyc") {
										openSimplifiedKyc(
											retry,
											() => onCreated(feeDisplay),
											nextError,
											{ forceOurKycRestart: true }
										)
									} else {
										openCardIssuanceError(nextError)
									}
								} else if (retryResponse.error.code === KYC_REQUIRED_ERROR_CODE) {
									openSimplifiedKyc(
										retry,
										() => onCreated(feeDisplay),
										{ ...retryResponse.error, issuer: issuerError.issuer }
									)
								} else {
									openCardIssuanceError(retryResponse.error)
								}
							})
						)
					} else {
						openCardIssuanceError(issuerError)
					}
					return res
				}

				if (res.error.code === KYC_REQUIRED_ERROR_CODE) {
					const issuerError = { ...res.error, issuer: res.error.issuer ?? info?.issuer ?? selectedBinIssuer ?? undefined }
					openSimplifiedKyc(
						() => GD.REQ_CREATE_CARD.request(payload),
						() => onCreated(feeDisplay),
						issuerError
					)
				}

				return res
			}}
			renderExtra={() => (
				<VBox gap='12px' sx={{ marginTop: "12px" }}>
					{hasAdvancedFields && <HBox
						role='button'
						tabIndex={0}
						aria-expanded={showAdvanced}
						onClick={() => setShowAdvanced(value => !value)}
						onKeyDown={event => (event.key === "Enter" || event.key === " ") && (event.preventDefault(), setShowAdvanced(value => !value))}
						sx={{
							alignItems: "center",
							justifyContent: "space-between",
							gap: "12px",
							padding: "12px 14px",
							borderRadius: "12px",
							border: "1px dashed #CBD5E1",
							cursor: "pointer",
							"&:hover": { borderColor: "#7FA3C6", backgroundColor: "#FAFAFF" }
						}}
					>
						<VBox gap='2px'>
							<Typo sx={{ fontWeight: 600, color: "#0F172A" }}>{showAdvanced ? "Hide advanced settings" : "Advanced settings"}</Typo>
							<Typo variant='caption' sx={{ color: "#64748B" }}>Spending limits, auto top-up, merchant rules · optional</Typo>
						</VBox>
						<svg aria-hidden width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='#2B5C8A' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' style={{ flexShrink: 0, transform: showAdvanced ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}><path d='M6 9l6 6 6-6' /></svg>
					</HBox>}
					{noBinOptions ? <Box role='status'><Typo sx={{ color: "#64748B", lineHeight: 1.5 }}>No cards are available for this network. Choose another network or issuer account.</Typo></Box> : renderKycGate()}
					{selectedInfo ? (
						<OrderSummary info={selectedInfo} amount={amount} fallbackCurrency={fallbackCurrency} issuerName={pricingIssuerName} showBin={!binPreset} />
					) : null}
				</VBox>
			)}
			extraBeforeActions
			ignoreObserver
			primaryButton={primaryButton}
			secondaryButton={
				onCancel ? { title: secondaryTitle, onClick: onCancel } : undefined
			}
		/>
			</Box>
		</>
	)
}
